import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import Layout from '../components/Layout';
import { mbcbService } from '../api/mbcbService';

const MbcbReportImport: React.FC = () => {
  const [csvText, setCsvText] = useState('');
  const [onDuplicate, setOnDuplicate] = useState<'skip' | 'update'>('skip');
  const [defaultStatus, setDefaultStatus] = useState('approved');
  const [lastResult, setLastResult] = useState<any>(null);

  const dryRunMutation = useMutation({
    mutationFn: () =>
      mbcbService.importReports({
        csvText,
        dryRun: true,
        onDuplicate,
        defaultStatus,
      }),
    onSuccess: (data) => setLastResult(data),
  });

  const commitMutation = useMutation({
    mutationFn: () =>
      mbcbService.importReports({
        csvText,
        dryRun: false,
        onDuplicate,
        defaultStatus,
      }),
    onSuccess: (data) => setLastResult(data),
  });

  return (
    <Layout>
      <div className="px-4 sm:px-0 max-w-4xl">
        <div className="mb-6">
          <Link to="/reports" className="text-sm text-primary-600 hover:text-primary-800">
            ← Back to MBCB reports
          </Link>
          <h1 className="mt-4 text-2xl font-bold text-gray-900">Import ministry reports (CSV)</h1>
          <p className="mt-2 text-sm text-gray-600">
            Paste a SurveyMonkey-style spreadsheet export: first row must be headers. Match planters using{' '}
            <code className="text-xs bg-gray-100 px-1 rounded">planter_id</code> or{' '}
            <code className="text-xs bg-gray-100 px-1 rounded">email</code> (planter or linked user), optionally{' '}
            <code className="text-xs bg-gray-100 px-1 rounded">church_name</code> when unique.
          </p>
          <p className="mt-2 text-xs text-gray-500">
            Required: <code>report_year</code>, <code>report_month</code>. Per-Sunday columns:{' '}
            <code>ministry_visits_s1</code>…<code>s5</code>, <code>professions_s1</code>…<code>s5</code>,{' '}
            <code>members_letter_s1</code>…<code>s5</code>, <code>members_baptism_s1</code>…<code>s5</code>,{' '}
            <code>bible_study_s1</code>…<code>s5</code>, <code>worship_s1</code>…<code>s5</code>. Giving:{' '}
            <code>income_congregational</code>, <code>income_sponsoring</code>, <code>income_association</code>,{' '}
            <code>income_mbcb</code>, <code>income_other</code>, <code>income_total</code>,{' '}
            <code>mission_cp</code>, <code>mission_association</code>, <code>mission_margaret_lackey</code>,{' '}
            <code>mission_other</code>, <code>mission_total</code>. Text: <code>ministry_highlights</code>,{' '}
            <code>prayer_requests</code>, <code>future_projects</code>, <code>dept_assistance</code>,{' '}
            <code>building_fund</code>.
          </p>
        </div>

        <div className="space-y-4 bg-white shadow rounded-lg p-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">CSV content</label>
            <textarea
              value={csvText}
              onChange={(e) => setCsvText(e.target.value)}
              rows={14}
              className="block w-full rounded-md border-gray-300 font-mono text-xs shadow-sm focus:border-primary-500 focus:ring-primary-500"
              placeholder="email,report_year,report_month,worship_s1,..."
            />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">If month already has a report</label>
              <select
                value={onDuplicate}
                onChange={(e) => setOnDuplicate(e.target.value as 'skip' | 'update')}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
              >
                <option value="skip">Skip row</option>
                <option value="update">Overwrite existing</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Imported status</label>
              <select
                value={defaultStatus}
                onChange={(e) => setDefaultStatus(e.target.value)}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
              >
                <option value="approved">approved</option>
                <option value="submitted">submitted</option>
                <option value="draft">draft</option>
              </select>
            </div>
          </div>
          <div className="flex flex-wrap gap-3">
            <button
              type="button"
              onClick={() => dryRunMutation.mutate()}
              disabled={!csvText.trim() || dryRunMutation.isPending || commitMutation.isPending}
              className="inline-flex justify-center rounded-md border border-transparent bg-primary-600 py-2 px-4 text-sm font-medium text-white shadow-sm hover:bg-primary-700 disabled:opacity-50"
            >
              {dryRunMutation.isPending ? 'Preview…' : 'Dry run (preview)'}
            </button>
            <button
              type="button"
              onClick={() => {
                if (
                  !window.confirm(
                    'Commit import to the database? This cannot be undone except by deleting reports manually.',
                  )
                ) {
                  return;
                }
                commitMutation.mutate();
              }}
              disabled={!csvText.trim() || dryRunMutation.isPending || commitMutation.isPending}
              className="inline-flex justify-center rounded-md border border-gray-300 bg-white py-2 px-4 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 disabled:opacity-50"
            >
              {commitMutation.isPending ? 'Importing…' : 'Commit import'}
            </button>
          </div>
        </div>

        {lastResult && (
          <div className="mt-6 bg-gray-50 border border-gray-200 rounded-lg p-4 text-sm">
            <h2 className="font-semibold text-gray-900 mb-2">Result</h2>
            <pre className="text-xs overflow-x-auto whitespace-pre-wrap">
              {JSON.stringify(lastResult, null, 2)}
            </pre>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default MbcbReportImport;
